import React, { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { SpinnerView } from "../elements/Spinners";

// Lazy load all shared containers used across personas
const OrganizationsStartupContainer = lazy(() =>
  import("../containers/Organizations/OrganizationsContainer").then((m) => ({
    default: m.OrganizationsStartupContainer,
  }))
);

const OrganizationsInvestorsContainer = lazy(() =>
  import("../containers/Organizations/OrganizationsContainer").then((m) => ({
    default: m.OrganizationsInvestorsContainer,
  }))
);

const OrganizationsHubContainer = lazy(() =>
  import("../containers/Organizations/OrganizationsContainer").then((m) => ({
    default: m.OrganizationsHubContainer,
  }))
);

const OrganizationsFundersContainer = lazy(() =>
  import("../containers/Organizations/OrganizationsContainer").then((m) => ({
    default: m.OrganizationsFundersContainer,
  }))
);

const FKIndustryPulseContainer = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKIndustryPulse/FKIndustryPulseContainer"
  ).then((m) => ({ default: m.FKIndustryPulseContainer }))
);

const FKMarketIntelligenceContainer = lazy(() =>
  import("../containers/FundingKit/FKMarketIntelligenceContainer").then((m) => ({
    default: m.FKMarketIntelligenceContainer,
  }))
);

const FKTrendDetailContainer = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKTrendDetail/FKTrendDetailContainer"
  ).then((m) => ({ default: m.FKTrendDetailContainer }))
);

const OrganizationContainer = lazy(() =>
  import("../containers/Organizations/OrganizationContainer").then((m) => ({
    default: m.default,
  }))
);

const DealsContainer = lazy(() =>
  import("../containers/Deals/DealsContainer").then((m) => ({
    default: m.default,
  }))
);

const EventsDirectory = lazy(() =>
  import("../containers/Events/EventsDirectory").then((m) => ({
    default: m.default,
  }))
);

const EventDetailsContainer = lazy(() =>
  import("../containers/Events/EventDetails").then((m) => ({
    default: m.EventDetailsContainer,
  }))
);

const MacroContainer = lazy(() =>
  import("../containers/Macro/MacroContainer").then((m) => ({
    default: m.MacroContainer,
  }))
);

const ResearchInsightsContainer = lazy(() =>
  import("../containers/ResearchInsights").then((m) => ({
    default: m.ResearchInsightsContainer,
  }))
);

const ResearchInsightContainer = lazy(() =>
  import("../containers/ResearchInsights").then((m) => ({
    default: m.ResearchInsightContainer,
  }))
);

const ProfileContainer = lazy(() =>
  import("../containers/Profile/ProfileContainer").then((m) => ({
    default: m.ProfileContainer,
  }))
);

const SettingsContainerAccount = lazy(() =>
  import("../containers/Settings/SettingsContainerAccount").then((m) => ({
    default: m.SettingsContainerAccount,
  }))
);

const SettingsContainerPassword = lazy(() =>
  import("../containers/Settings/SettingsContainerAccount").then((m) => ({
    default: m.SettingsContainerPassword,
  }))
);

const SettingsContainerPreferences = lazy(() =>
  import("../containers/Settings/SettingsContainerAccount").then((m) => ({
    default: m.SettingsContainerPreferences,
  }))
);

const SettingsContainerOrganizations = lazy(() =>
  import("../containers/Settings/SettingsContainerOrganizations").then((m) => ({
    default: m.SettingsContainerOrganizations,
  }))
);

const FundsDirectoryContainer = lazy(() =>
  import("../containers/Funds/FundsDirectory").then((m) => ({
    default: m.FundsDirectoryContainer,
  }))
);

const BIAssistantContainer = lazy(() =>
  import("../containers/Assistant/AssistantContainer").then((m) => ({
    default: m.BIAssistantContainer,
  }))
);

const ChatHistoryContainer = lazy(() =>
  import("../containers/Assistant/ChatHistoryContainer").then((m) => ({
    default: m.ChatHistoryContainer,
  }))
);

// Wrap each component with Suspense for consistent loading UX
const withSuspense = (
  Component: React.LazyExoticComponent<any>,
  props?: any
) => (
  <Suspense fallback={<SpinnerView message="Loading..." />}>
    <Component {...props} />
  </Suspense>
);

export const sharedRoutes = [
  { path: "/", element: <Navigate to="/companies" /> },

  // Organizations
  {
    path: "/companies",
    element: withSuspense(OrganizationsStartupContainer, { types: ["startup"] }),
  },
  {
    path: "/investors",
    element: withSuspense(OrganizationsInvestorsContainer, {
      types: ["investor"],
    }),
  },
  {
    path: "/esos",
    element: withSuspense(OrganizationsHubContainer, { types: ["hub"] }),
  },
  {
    path: "/funding-organizations",
    element: withSuspense(OrganizationsFundersContainer, {
      types: ["hub", "investor", "ngo_charity"],
    }),
  },

  // Market Intelligence (shared between FOUNDER and other personas)
  {
    path: "/industry-pulse",
    element: withSuspense(FKIndustryPulseContainer),
  },
  {
    path: "/market-intelligence",
    element: withSuspense(FKMarketIntelligenceContainer),
  },
  {
    path: "/market-intelligence/trends/:trendId",
    element: withSuspense(FKTrendDetailContainer),
  },

  // Organization details
  {
    path: "/organizations/:id",
    element: withSuspense(OrganizationContainer),
  },

  // Deals
  { path: "/deals", element: withSuspense(DealsContainer) },

  // Events
  { path: "/events", element: withSuspense(EventsDirectory) },
  { path: "/events/:id", element: withSuspense(EventDetailsContainer) },

  // Comparison tool (MacroContainer)
  {
    path: "/comparison",
    element: (
      <Suspense fallback={<SpinnerView message="Loading comparison tool..." />}>
        <MacroContainer />
      </Suspense>
    ),
  },

  // Research
  { path: "/research", element: withSuspense(ResearchInsightsContainer) },
  {
    path: "/research/resources/:id",
    element: withSuspense(ResearchInsightContainer),
  },

  // Profile
  { path: "/profile", element: withSuspense(ProfileContainer) },

  // Settings
  { path: "/settings", element: <Navigate to="/settings/account" /> },
  {
    path: "/settings/account",
    element: withSuspense(SettingsContainerAccount),
  },
  {
    path: "/settings/password",
    element: withSuspense(SettingsContainerPassword),
  },
  {
    path: "/settings/preferences",
    element: withSuspense(SettingsContainerPreferences),
  },
  {
    path: "/settings/organizations",
    element: withSuspense(SettingsContainerOrganizations),
  },

  // Funds
  { path: "/funds", element: withSuspense(FundsDirectoryContainer) },

  // Assistant
  { path: "/assistant", element: withSuspense(BIAssistantContainer) },
  { path: "/assistant/c/:id", element: withSuspense(BIAssistantContainer) },
  { path: "/assistant/history", element: withSuspense(ChatHistoryContainer) },

];
